// src/components/home/awareness/WalletActivationAwarenessCampaign.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import api from '../../../api';
import { startPiTopUp } from '../../../utils/piTopUp';

import WalletActivationAwarenessPopup from '../../popups/WalletActivationAwarenessPopup';

import {
  clearAwarenessCooldown,
  hasAwarenessCooldown,
  setAwarenessCooldown,
} from './awarenessStorage';

const CAMPAIGN_KEY = 'wallet_activation';

const SEVEN_DAYS_MS =
  7 * 24 * 60 * 60 * 1000;

const DEFAULT_OPEN_DELAY_MS = 900;

const getActivationAmount = () => {
  const value = Number(
    process.env.REACT_APP_MIN_MAINNET_ACTIVATION_PI || 0.1
  );

  if (!Number.isFinite(value) || value <= 0) {
    return 0.1;
  }

  return value;
};

const getDepositTotal = (history = []) => {
  if (!Array.isArray(history)) {
    return 0;
  }

  return history
    .filter((transaction) => {
      const type = String(
        transaction?.type || ''
      ).toLowerCase();

      return (
        type === 'deposit' ||
        type === 'topup'
      );
    })
    .reduce((total, transaction) => {
      return (
        total +
        Number(transaction?.amount || 0)
      );
    }, 0);
};

const isMainnetEnvironment = () => {
  const hostname = String(
    window.location.hostname || ''
  ).toLowerCase();

  return (
    hostname.includes('barterfi.app') &&
    !hostname.includes('testnet.')
  );
};

function WalletActivationAwarenessCampaign({
  user,

  enabled = false,

  onEligibilityResolved,
  onOpened,
  onFinished,

  openDelayMs = DEFAULT_OPEN_DELAY_MS,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [errorMessage, setErrorMessage] =
    useState('');

  const [successMessage, setSuccessMessage] =
    useState('');

  const checkedRef = useRef(false);
  const completedRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const username = String(
    user?.username || ''
  )
    .trim()
    .toLowerCase();

  const accountType = String(
    user?.accountType || 'primary'
  ).toLowerCase();

  const activationAmount =
    getActivationAmount();

  const resolveEligibility = useCallback(
    (eligible) => {
      onEligibilityResolved?.({
        campaignKey: CAMPAIGN_KEY,
        eligible: Boolean(eligible),
      });
    },
    [onEligibilityResolved]
  );

  const clearRedirectTimer = useCallback(() => {
    if (!redirectTimerRef.current) {
      return;
    }

    window.clearTimeout(
      redirectTimerRef.current
    );

    redirectTimerRef.current = null;
  }, []);

  /**
   * Reset campaign state when switching identities.
   */
  useEffect(() => {
    clearRedirectTimer();

    setIsOpen(false);
    setLoading(false);
    setErrorMessage('');
    setSuccessMessage('');

    checkedRef.current = false;
    completedRef.current = false;

    return () => {
      clearRedirectTimer();
    };
  }, [
    username,
    clearRedirectTimer,
  ]);

  /**
   * Manager temporarily disabled this campaign.
   *
   * This must not count as the user pressing Pass.
   */
  useEffect(() => {
    if (enabled || !isOpen) {
      return;
    }

    setIsOpen(false);
    setLoading(false);
    setErrorMessage('');
    setSuccessMessage('');
  }, [
    enabled,
    isOpen,
  ]);

  /**
   * Allow a fresh eligibility request during the next manager cycle.
   */
  useEffect(() => {
    if (enabled || isOpen) {
      return;
    }

    checkedRef.current = false;
    completedRef.current = false;
  }, [
    enabled,
    isOpen,
  ]);

  /**
   * Check whether this account still needs wallet activation.
   */
  useEffect(() => {
    if (!username) {
      return undefined;
    }

    /*
     * Linked app identities do not receive personal wallet prompts.
     */
    if (accountType === 'app') {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    /*
     * The activation campaign only applies to the mainnet app.
     */
    if (!isMainnetEnvironment()) {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    if (!enabled) {
      return undefined;
    }

    if (checkedRef.current) {
      return undefined;
    }

    checkedRef.current = true;

    /*
     * User previously pressed I'll Pass.
     */
    if (
      hasAwarenessCooldown(
        CAMPAIGN_KEY,
        username
      )
    ) {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const checkEligibility = async () => {
      try {
        const response = await api.get(
          `/wallet/${encodeURIComponent(
            user.username
          )}`
        );

        if (cancelled) {
          return;
        }

        const history = Array.isArray(
          response?.data?.history
        )
          ? response.data.history
          : [];

        const depositTotal =
          getDepositTotal(history);

        const eligible =
          depositTotal < activationAmount;

        console.log(
          '🔐 Wallet awareness eligibility:',
          {
            username,
            depositTotal,
            activationAmount,
            eligible,
          }
        );

        resolveEligibility(eligible);

        if (!eligible) {
          setIsOpen(false);

          /*
           * Activation is permanent based on deposit history,
           * so an old dismissal cooldown is no longer needed.
           */
          clearAwarenessCooldown(
            CAMPAIGN_KEY,
            username
          );

          return;
        }

        completedRef.current = false;

        setErrorMessage('');
        setSuccessMessage('');
        setIsOpen(true);

        onOpened?.({
          campaignKey: CAMPAIGN_KEY,
        });
      } catch (error) {
        console.error(
          'Failed to check wallet activation awareness:',
          error
        );

        if (!cancelled) {
          setIsOpen(false);

          /*
           * Do not block the banner campaign if wallet lookup fails.
           */
          resolveEligibility(false);
        }
      }
    };

    timerId = window.setTimeout(() => {
      void checkEligibility();
    }, Math.max(
      0,
      Number(openDelayMs) || 0
    ));

    return () => {
      cancelled = true;

      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [
    username,
    user?.username,
    accountType,
    enabled,
    activationAmount,
    openDelayMs,
    onOpened,
    resolveEligibility,
  ]);

  /**
   * User presses I'll Pass.
   */
  const handlePassed = useCallback(() => {
    if (!username || loading) {
      return;
    }

    setAwarenessCooldown({
      campaignKey: CAMPAIGN_KEY,
      username,
      durationMs: SEVEN_DAYS_MS,
    });

    setIsOpen(false);
    setErrorMessage('');
    setSuccessMessage('');

    onFinished?.({
      campaignKey: CAMPAIGN_KEY,
      outcome: 'dismissed',
    });
  }, [
    username,
    loading,
    onFinished,
  ]);

  /**
   * Complete the campaign only once.
   *
   * Recovery can trigger both onRecovered and onSuccess inside
   * startPiTopUp, so completedRef protects against duplicate finish
   * and navigation calls.
   */
  const completeActivation = useCallback(
    (result) => {
      if (completedRef.current) {
        return;
      }

      completedRef.current = true;

      clearAwarenessCooldown(
        CAMPAIGN_KEY,
        username
      );

      setLoading(false);

      setSuccessMessage(
        result?.message ||
          `Your BarterFi wallet has been activated with ${activationAmount} Pi.`
      );

      onFinished?.({
        campaignKey: CAMPAIGN_KEY,
        outcome: 'completed',
        action: 'wallet_activation',
        response: result || null,
      });

      clearRedirectTimer();

      redirectTimerRef.current =
        window.setTimeout(() => {
          redirectTimerRef.current = null;

          setIsOpen(false);

          navigate('/my-wallet', {
            replace: false,
            state: {
              from: 'wallet-awareness',
              walletActivated: true,
            },
          });
        }, 900);
    },
    [
      activationAmount,
      clearRedirectTimer,
      navigate,
      onFinished,
      username,
    ]
  );

  /**
   * Start the existing Pi SDK top-up flow.
   */
  const handleActivate = useCallback(async () => {
    if (
      loading ||
      completedRef.current ||
      !user?.username
    ) {
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const result = await startPiTopUp({
        amount: activationAmount,
        username: user.username,
        t,

        onWalletRefresh: async () => {
          /*
           * Confirm the wallet endpoint reflects the completed top-up.
           */
          await api.get(
            `/wallet/${encodeURIComponent(
              user.username
            )}`
          );
        },

        onRecovered: (recovered) => {
          completeActivation(recovered);
        },

        onSuccess: (success) => {
          completeActivation(success);
        },

        onCancel: (cancelled) => {
          setLoading(false);

          setErrorMessage(
            cancelled?.message ||
              'The payment was cancelled. You can try again when ready.'
          );
        },

        onError: (error) => {
          setLoading(false);

          setErrorMessage(
            error?.message ||
              'We could not activate your wallet. Please try again.'
          );
        },
      });

      /*
       * Some validation failures return without reaching callbacks.
       */
      if (
        !result?.ok &&
        !completedRef.current
      ) {
        setLoading(false);

        if (!result?.cancelled) {
          setErrorMessage(
            result?.message ||
              'We could not activate your wallet. Please try again.'
          );
        }
      }
    } catch (error) {
      console.error(
        'Failed to start wallet activation:',
        error
      );

      setLoading(false);

      setErrorMessage(
        error?.message ||
          'We could not activate your wallet. Please try again.'
      );
    }
  }, [
    activationAmount,
    completeActivation,
    loading,
    t,
    user?.username,
  ]);

  return (
    <WalletActivationAwarenessPopup
      open={isOpen}
      amount={activationAmount}
      loading={loading}
      errorMessage={errorMessage}
      successMessage={successMessage}
      onActivate={handleActivate}
      onPassed={handlePassed}
    />
  );
}

export default WalletActivationAwarenessCampaign;
